<template>
  <div>
    <loading :active.sync="isLoading" :is-full-page="true" color="#4e97b2"></loading>
    <h1>{{ $store.state.auth.convertAccount ? 'Confirm your Information' : 'Sign Up' }}</h1>
    <div v-if="created">
      <div class="container text-left">
        <div class="row justify-content-md-center">
          <div class="col-lg-5">
            Thank you for
            {{ $store.state.auth.convertAccount ?
              'updating your information.' :
              'signing up to be an Alabama Travel Partner.' }}
            <br />
            We have sent a code to your email. Please confirm this code to continue.
            <form @submit.prevent="onConfirmCode">
              <b-form-group>
                <b-form-input id="confirmCode" v-model="confirmCode" required placeholder="Confirmation Code"
                  type="text" />

                <b-button type=submit class="btn btn-secondary mt-4">Confirm My Code</b-button>
              </b-form-group>
            </form>
          </div>
        </div>
      </div>
    </div>
    <div v-else>
      <div v-if="$store.state.auth.convertAccount">
        We need to update your password. While we are in the process of doing that, please confirm your information.
      </div>
      <div class="container text-left">
        <div class="row justify-content-md-center">
          <div class="col-lg-5">
            <b-form @submit.prevent="onSubmit">
              <b-form-group label="First Name" label-for="firstName">
                <b-form-input id="firstName" v-model="form.firstName" placeholder="First Name" type="text"
                  :state="submitted && $v.form.firstName.$error ? false : null" />
                <b-form-invalid-feedback v-if="submitted && !$v.form.firstName.required">First Name is
                  required</b-form-invalid-feedback>
              </b-form-group>

              <b-form-group label="Last Name" label-for="lastName">
                <b-form-input id="lastName" v-model="form.lastName" placeholder="Last Name" type="text"
                  :state="submitted && $v.form.lastName.$error ? false : null" />
                <b-form-invalid-feedback v-if="submitted && !$v.form.lastName.required">Last Name is
                  required</b-form-invalid-feedback>
              </b-form-group>

              <TelephoneInput v-model="form.telephone" :submitted="submitted" :required="true" :v="$v.form.telephone" />

              <b-form-group label="Email" label-for="email">
                <b-form-input id="email" name="email" v-model="form.email" placeholder="Email" type="email"
                  :state="submitted && $v.form.email.$error ? false : null"
                  :disabled="$store.state.auth.convertAccount" />
                <b-form-invalid-feedback v-if="submitted && !$v.form.email.required">Email is
                  required</b-form-invalid-feedback>
                <b-form-invalid-feedback v-if="submitted && !$v.form.email.email">Email needs to be a valid
                  email</b-form-invalid-feedback>
              </b-form-group>

              <b-form-group label="Confirm Email" label-for="confirmEmail" v-if="!$store.state.auth.convertAccount">
                <b-form-input id="confirmEmail" name="confirmEmail" v-model="form.confirmEmail"
                  placeholder="Confirm Email" type="email"
                  :state="submitted && $v.form.confirmEmail.$error ? false : null" />
                <b-form-invalid-feedback v-if="submitted && !$v.form.confirmEmail.required">Confirm Email is
                  required</b-form-invalid-feedback>
                <b-form-invalid-feedback v-if="submitted && !$v.form.confirmEmail.sameAs">Confirm Email needs to be the
                  same as the email above</b-form-invalid-feedback>
              </b-form-group>

              <b-form-group label="Password" label-for="password">
                <b-form-input id="password" name="password" v-model="form.password" placeholder="Password" type="password"
                  :state="submitted && $v.form.password.$error ? false : null" />
                <b-form-invalid-feedback v-if="submitted && !$v.form.password.required">Password is
                  required</b-form-invalid-feedback>
                <b-form-invalid-feedback v-if="submitted && !$v.form.password.minLength">Password needs to be at least 10
                  characters</b-form-invalid-feedback>
              </b-form-group>

              <b-form-group label="Confirm Password" label-for="confirmPassword">
                <b-form-input id="confirmPassword" name="confirmPassword" v-model="form.confirmPassword"
                  placeholder="Confirm Password" type="password"
                  :state="submitted && $v.form.confirmPassword.$error ? false : null" />
                <b-form-invalid-feedback v-if="submitted && !$v.form.confirmPassword.required">Confirm Password is
                  required</b-form-invalid-feedback>
                <b-form-invalid-feedback v-if="submitted && !$v.form.confirmPassword.sameAs">Confirm Password needs to be
                  the same as the password entered above</b-form-invalid-feedback>
                <b-form-invalid-feedback v-if="submitted && !$v.form.confirmPassword.minLength">Confirm Password needs to
                  be at least 10 characters</b-form-invalid-feedback>
              </b-form-group>

              <b-form-group v-if="!$store.state.auth.convertAccount">
                <b-form-checkbox v-model="form.terms" id="terms" name="terms" value="terms"
                  :state="submitted && !form.terms ? false : null">I agree to the <router-link :to="{ name: 'terms' }"
                    target="_blank">Partner Requirements and Terms</router-link></b-form-checkbox>
                <b-form-invalid-feedback v-if="submitted && !form.terms">Agreeing to the requirements and terms is
                  required</b-form-invalid-feedback>
              </b-form-group>

              <b-form-group v-if="!$store.state.auth.convertAccount">
                <b-form-checkbox v-model="form.news" id="news" name="news" value="news">Sign-up for Industry
                  News</b-form-checkbox>
              </b-form-group>

              <b-button type=submit :disabled="submitted && $v.$invalid">
                {{ $store.state.auth.convertAccount ? "Update My Account" : "Create My Account" }}
              </b-button>

              <b-form-invalid-feedback v-if="processErrors">{{ processErrors }}</b-form-invalid-feedback>
            </b-form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/vue-loading.css';
import {
  email,
  maxLength,
  minLength,
  required,
  sameAs,
} from 'vuelidate/lib/validators';
import TelephoneInput from '../../components/TelephoneInput.vue';

export default {
  components: {
    Loading,
    TelephoneInput
  },
  data() {
    return {
      isLoading: false,
      form: {
        firstName: '',
        lastName: '',
        telephone: '',
        email: '',
        password: '',
        terms: false,
        news: false,
        confirmEmail: '',
        confirmPassword: '',
      },
      submitted: false,
      processErrors: null,
      created: false,
      confirmCode: '',
    };
  },
  validations: {
    form: {
      firstName: {
        required,
      },
      lastName: {
        required,
      },
      telephone: {
        required,
        minLength: minLength(14),
        maxLength: maxLength(17),
      },
      email: {
        required,
        email,
      },
      password: {
        required,
        minLength: minLength(10),
      },
      confirmEmail: {
        required,
        sameAs: sameAs('email'),
      },
      confirmPassword: {
        required,
        sameAs: sameAs('password'),
        minLength: minLength(10),
      },
    },
  },
  beforeMount() {
    if (this.$store.state.auth.convertAccount) {
      this.form.firstName = this.$store.state.auth.user.first_name;
      this.form.lastName = this.$store.state.auth.user.last_name;
      this.form.telephone = this.$store.state.auth.user.telephone;
      this.form.email = this.$store.state.auth.user.email;
      this.form.confirmEmail = this.$store.state.auth.user.email;
      this.form.terms = true;
    }
  },
  methods: {
    async onSubmit() {
      this.submitted = true;
      this.$v.$touch();
      if (this.$v.$invalid || !this.form.terms) {
        return;
      }
      try {
        this.isLoading = true;
        await this.$store.dispatch('auth/signUp', {
          username: this.form.email.toLowerCase(),
          password: this.form.password,
          attributes: {
            email: this.form.email.toLowerCase(),
            phone_number: `${this.form.telephone.replace(/\D/g, '')}`,
            given_name: this.form.firstName,
            family_name: this.form.lastName,
          },
        }).then(async () => {
          this.created = true;
          await axios.post(`${process.env.VUE_APP_API_USERS_ROOT}/users`, {
            email: this.form.email.toLowerCase(),
            firstName: this.form.firstName,
            lastName: this.form.lastName,
            telephone: this.form.telephone.replace(/\D/g, ''),
            news: this.form.news === 'news',
          });
        }).catch((err) => {
          this.$bvToast.toast(err.message);
          this.processErrors = err;
        });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.log(err);
      } finally {
        this.isLoading = false;
        await axios.post(`${process.env.VUE_APP_SUBSCRIBE_API}addSubscriber`, {
          email: this.form.email,
          first_name: this.form.firstName,
          last_name: this.form.lastName,
        }, {
          headers: {
            'Access-Control-Allow-Origin': '*',
            Accept: 'application/json',
          },
        });
      }
    },
    onConfirmCode() {
      this.$router.push(`/auth/confirmCode/${this.form.email.toLowerCase()}/${this.confirmCode}`);
    },
  },
};
</script>

<style scoped>
div>>>.d-block {
  font-weight: 700;
}

button {
  background-color: #4e97b2;
  position: relative;
  font-weight: 600;
  border: none;
  color: white;
}

button:hover {
  background-color: #357e99;
}
</style>
